import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { APIService } from '../../services/api/api.service';

import * as fromOrders from './order.reducer';
import * as OrdersSelectors from './order.selectors';
import * as OrdersActions from './order.actions';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import {
  map,
  catchError,
  switchMap,
  tap,
  withLatestFrom,
  mergeMap,
  debounce
} from 'rxjs/operators';
import { of, timer, asyncScheduler, Observable, forkJoin } from 'rxjs';
import { Action } from '@ngrx/store';
import { NzModalService } from 'ng-zorro-antd/modal';
import { DrawerService } from '../../services/drawer/drawer.service';
import { OrdersFacade } from './order.facade';
import {
  DrawerType,
  IGetOrdersDTO,
  IGetOrdersQuery,
  OrderPrescriptionType,
  IOrder
} from '@care-portals/api-interfaces';
import { downloadCSV } from '@care-portals/shared';
import { PaginationCache } from '../../components/common/pagination/pagination-cache';
import { swrQuery } from '../../components/common/pagination/swr-query';

@Injectable()
export class OrdersEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly notification: NzNotificationService,
    private readonly api: APIService,
    private readonly nzModalService: NzModalService,
    private readonly store: Store<fromOrders.OrdersPartialState>,
    private readonly drawerService: DrawerService,
    private readonly ordersFacade: OrdersFacade
  ) {}

  private readonly pageCache = new PaginationCache<IOrder>();

  loadOrders$ = createEffect(() => {
    // Filter/sort changes ('new') are debounced so typing doesn't spam the API;
    // page clicks, load-more and drawer navigation fire immediately. Keeping this
    // as a single stream means a newer action (e.g. a page click) cancels a still
    // pending debounced 'new' via debounce + the switchMap below — so a delayed
    // 'new' can't fire late and snap the list back to page 1.
    const isDebounced = (loadType?: string) => !loadType || loadType === 'new';

    return this.actions$.pipe(
      ofType(OrdersActions.loadOrders),
      debounce(({ loadType }) => timer(isDebounced(loadType) ? 500 : 0)),
      withLatestFrom(
        this.store.select(OrdersSelectors.getMinMaxIds),
        this.store.select(OrdersSelectors.getOrdersFilter)
      ),
      switchMap(
        ([
          { loadType, filters, openNext, openPrevious },
          stateMinMax,
          stateFilter
        ]) => {
          type OrdersPage = {
            data: IOrder[];
            totalCount?: number;
            hasMore?: boolean;
          };
          type OrdersListResponse = OrdersPage | IOrder[];

          const query = { ...filters };
          // Derive limit/offset deterministically from the action (the component
          // always sends them for non-archived) so the cache key is stable.
          const limit = Number(filters.limit || stateFilter.limit || 25);
          let offset = Number((filters as IGetOrdersDTO).offset ?? 0);
          if (loadType === 'new') offset = 0;

          // Serving cached data (openNext/openPrevious drawer navigation included).
          const onPage = (res: OrdersPage): void => {
            const orders = res.data;
            if (openNext && loadType === 'page' && orders?.length) {
              this.store.dispatch(
                OrdersActions.switchOrderSuccess({ order: orders[0] })
              );
            } else if (openPrevious && loadType === 'page' && orders?.length) {
              this.store.dispatch(
                OrdersActions.switchOrderSuccess({
                  order: orders[orders.length - 1]
                })
              );
            } else if (openNext) {
              this.store.dispatch(
                OrdersActions.switchOrder({ direction: 'next' })
              );
            }
          };

          const toSuccess = (res: OrdersPage) =>
            OrdersActions.loadOrdersSuccess({
              loadType,
              orders: res.data,
              totalCount: Number(res.totalCount),
              filters,
              hasMore: res.hasMore ?? false
            });

          if (!query.includeArchived) {
            filters = { ...filters, limit, offset };
            const requestQuery = { ...query, limit, offset } as IGetOrdersDTO;
            const cacheKey = PaginationCache.buildKey(
              requestQuery as Record<string, unknown>
            );

            return swrQuery<IOrder, Action>({
              cache: this.pageCache,
              key: cacheKey,
              // Serve cached pages on a macrotask so the table's nzLoading spinner
              // paints before the (synchronous) cached render, instead of the list
              // appearing frozen while the rows re-render with no feedback.
              serveScheduler: asyncScheduler,
              fetch: () =>
                this.api.getAllOrders(requestQuery).pipe(
                  map((res: OrdersListResponse) =>
                    Array.isArray(res)
                      ? { data: res, totalCount: undefined, hasMore: false }
                      : res
                  )
                ),
              onServe: page => onPage(page),
              onSuccess: page => toSuccess(page),
              onError: error => {
                console.error('Error', error);
                this.notification.error(
                  'Error',
                  'There was an error loading orders.'
                );
                return of(OrdersActions.loadOrdersFailure({ error }));
              }
            });
          }

          // Archived orders: legacy cursor endpoint, no caching.
          if (loadType === 'older') {
            Object.assign(query, { maxOrderId: stateMinMax?.min - 1 });
          }
          return this.api
            .getAllArchivedOrders({ ...query, limit } as IGetOrdersQuery)
            .pipe(
              map((res: OrdersListResponse) => {
                if (Array.isArray(res)) {
                  if (!res.length && loadType === 'older') {
                    this.notification.info('Info', 'There are no more orders');
                  }
                  return { data: res, totalCount: undefined, hasMore: false };
                }
                return res;
              }),
              tap(res => onPage(res)),
              map(toSuccess),
              catchError(error => {
                console.error('Error', error);
                this.notification.error(
                  'Error',
                  'There was an error loading orders.'
                );
                return of(OrdersActions.loadOrdersFailure({ error }));
              })
            );
        }
      )
    );
  });

  // Any mutation can change what a page contains, so drop the cached pages.
  invalidatePageCache$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          OrdersActions.createOrderSuccess,
          OrdersActions.updateOrderSuccess,
          OrdersActions.chargeOrderSuccess,
          OrdersActions.chargeOrderFailure,
          OrdersActions.retriggerOrderSuccess,
          OrdersActions.retriggerPaymentSuccess,
          OrdersActions.updateOrderPrescriptionSuccess
        ),
        tap(() => this.pageCache.clear())
      ),
    { dispatch: false }
  );

  loadOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrdersActions.loadOrder),
      mergeMap(action =>
        this.api.getOrder(action.orderId).pipe(
          map(res => {
            if (!res?.id) {
              throw new Error(`Order #${action.orderId} does not exist.`);
            }
            return OrdersActions.loadOrderSuccess({ order: res });
          }),
          catchError(error => {
            console.error('Error', error);
            this.notification.error(
              'Error',
              error.message || 'There was an error loading order.'
            );
            this.drawerService.closeDrawer(DrawerType.ORDER_DETAILS);
            return of(OrdersActions.loadOrderFailure({ error }));
          })
        )
      )
    )
  );

  loadOrderSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrdersActions.loadOrderSuccess, OrdersActions.switchOrderSuccess),
      switchMap(({ order }) => {
        if (!order?.id) {
          return of(
            OrdersActions.loadOrderPrescriptionSuccess({
              order,
              rxResults: {
                prescriptions: [],
                type: 'related'
              },
              failedWorkflowRuns: []
            })
          );
        }

        return forkJoin({
          rxResults: this.api.getOrderPrescriptions(order.id.toString()).pipe(
            catchError(error => {
              console.error('OrderEffects :: loadOrderPrescriptions()', error);
              return of({
                prescriptions: [],
                type: 'related' as OrderPrescriptionType
              });
            })
          ),
          failedWorkflowRuns: this.api
            .getOrderFailedWorkflowRuns(
              order.id.toString(),
              order._id?.toString()
            )
            .pipe(
              catchError(error => {
                console.error(
                  'OrderEffects :: loadOrderFailedWorkflowRuns()',
                  error
                );
                return of([]);
              })
            )
        }).pipe(
          map(({ rxResults, failedWorkflowRuns }) =>
            OrdersActions.loadOrderPrescriptionSuccess({
              order,
              rxResults: {
                prescriptions: rxResults.prescriptions || [],
                type: rxResults.type
              },
              failedWorkflowRuns
            })
          )
        );
      })
    )
  );

  createOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrdersActions.createOrder),
      switchMap(action =>
        this.api
          .createOrder(
            action.orderItems,
            action.customerId,
            action.isRefill,
            action.delayedCapture,
            action.sendEvent
          )
          .pipe(
            map(order => OrdersActions.createOrderSuccess({ order })),
            catchError(error => {
              return of(OrdersActions.createOrderFailure({ error }));
            })
          )
      )
    )
  );

  createOrderSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OrdersActions.createOrderSuccess),
        tap(({ order }) => {
          if (order.paymentError) {
            if (order.paymentError.status === 'requires_action') {
              this.notification.warning(
                'Additional Authentication Required',
                'This payment requires additional authentication.'
              );
            } else {
              this.notification.error(
                'Payment Failed',
                order.paymentError.message || 'Failed to process payment'
              );
            }
          }

          const isDelayedCapture = order.payment?.status === 'delayed_capture';

          this.nzModalService.confirm({
            nzTitle: `Order #${order.id} created successfully. ${
              isDelayedCapture
                ? 'Delayed Capture Payment Created'
                : order.paymentError
                ? order.paymentError.status === 'requires_action'
                  ? 'But requires additional authentication'
                  : order.paymentError.status === 'failed'
                  ? 'But Payment Failed'
                  : ', Payment Successful'
                : 'Payment Successful'
            }`,
            nzContent: `${
              order.paymentError
                ? order.paymentError.status === 'requires_action'
                  ? 'Please complete the authentication process to finalize the payment. '
                  : order.paymentError.status === 'failed'
                  ? 'You can retry the payment later. '
                  : ''
                : ''
            }Do you want to view the new order details?`,
            nzOnOk: () => {
              this.ordersFacade.openOrderDetails(String(order.id));
            }
          });
        })
      ),
    { dispatch: false }
  );

  updateOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrdersActions.updateOrder),
      switchMap(action =>
        this.api.updateOrder(action.orderId, action.data).pipe(
          tap(() => this.notification.success('Successfully saved.', '')),
          map(order => {
            const update = {
              id: action.orderId,
              changes: { ...order }
            };
            return OrdersActions.updateOrderSuccess({ update });
          }),
          catchError(error => {
            console.error('Error', error);
            this.notification.error(
              'Error',
              'There was an error updating order.'
            );
            return of(OrdersActions.updateOrderFailure({ error }));
          })
        )
      )
    )
  );

  switchOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrdersActions.switchOrder),
      withLatestFrom(
        this.store.select(OrdersSelectors.getActiveOrderIndex),
        this.store.select(OrdersSelectors.getOrdersEntities),
        this.store.select(OrdersSelectors.selectOrdersIds),
        this.store.select(OrdersSelectors.getOrdersFilter),
        this.store.select(OrdersSelectors.getOrdersCount)
      ),
      map(
        ([
          action,
          activeOrderIndex,
          ordersEntities,
          ordersIds,
          ordersFilter,
          ordersCount
        ]) => {
          const indexStep = action.direction === 'next' ? 1 : -1;
          const nextOrderId = ordersIds[activeOrderIndex + indexStep];
          const nextOrder = ordersEntities[nextOrderId];

          if (!nextOrder) {
            const filters = ordersFilter as IGetOrdersDTO;
            const limit = Number(filters.limit || 25);
            const offset = Number(filters.offset || 0);

            if (!filters.includeArchived && action.direction === 'next') {
              const total = Number(ordersCount || 0);
              if (offset + limit < total) {
                return OrdersActions.loadOrders({
                  loadType: 'page',
                  filters: { ...filters, offset: offset + limit },
                  openNext: true
                });
              }
            }

            if (!filters.includeArchived && action.direction === 'previous') {
              if (offset > 0) {
                return OrdersActions.loadOrders({
                  loadType: 'page',
                  filters: {
                    ...filters,
                    offset: Math.max(0, offset - limit)
                  },
                  openPrevious: true
                });
              }
            }

            const loadType = action.direction === 'next' ? 'older' : 'newer';
            return OrdersActions.loadOrders({
              loadType,
              filters: ordersFilter,
              openNext: action.direction === 'next'
            });
          }

          return OrdersActions.switchOrderSuccess({ order: nextOrder });
        }
      )
    )
  );

  chargeOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrdersActions.chargeOrder),
      switchMap(({ orderId, customerId }) =>
        this.api.chargeOrder(orderId, customerId).pipe(
          map(res => {
            if (res.status === 'paid') {
              return OrdersActions.chargeOrderSuccess({ orderId });
            } else {
              return OrdersActions.chargeOrderFailure({ orderId, error: res });
            }
          }),
          catchError(error => {
            console.error('Error charging order:', error);
            return of(OrdersActions.chargeOrderFailure({ orderId, error }));
          })
        )
      )
    )
  );

  chargeOrderSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OrdersActions.chargeOrderSuccess),
        tap(({ orderId }) => {
          this.store.dispatch(
            OrdersActions.loadOrder({ orderId: Number(orderId) })
          );
          this.notification.success(
            'Payment successful',
            'Payment processed successfully'
          );
        })
      ),
    { dispatch: false }
  );

  chargeOrderFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OrdersActions.chargeOrderFailure),
        tap(({ orderId, error }) => {
          this.store.dispatch(
            OrdersActions.updateOrder({
              orderId: Number(orderId),
              data: { status: 'awaiting_payment' }
            })
          );
          this.notification.error(
            'Payment failed',
            error.message || 'Failed to process payment'
          );
        })
      ),
    { dispatch: false }
  );

  exportOrders$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrdersActions.exportOrders),
      switchMap(({ filters }) =>
        this.api.exportOrders(filters).pipe(
          tap(res => {
            downloadCSV(res, 'orders.csv');
          }),
          map(() => OrdersActions.exportOrdersSuccess({ orders: [] })),
          catchError(error => {
            console.error('Error exporting orders:', error);
            this.notification.error(
              'Error',
              'There was an error exporting orders.'
            );
            return of(OrdersActions.exportOrdersFailure({ error }));
          })
        )
      )
    )
  );

  retriggerOrderStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrdersActions.retriggerOrder),
      switchMap(({ orderId }) =>
        this.api.retriggerStatus(orderId, 'order').pipe(
          tap(() =>
            this.notification.success(
              'Order status retriggered successfully.',
              ''
            )
          ),
          map(order => OrdersActions.retriggerOrderSuccess({ order })),
          catchError(error => {
            console.error('Error', error);
            this.notification.error(
              'Error',
              'There was an error retriggering order status.'
            );
            return of(OrdersActions.retriggerOrderFailure({ error }));
          })
        )
      )
    )
  );

  retriggerPaymentStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrdersActions.retriggerPayment),
      switchMap(({ orderId }) =>
        this.api.retriggerStatus(orderId, 'payment').pipe(
          tap(() =>
            this.notification.success(
              'Payment status retriggered successfully.',
              ''
            )
          ),
          map(order => OrdersActions.retriggerPaymentSuccess({ order })),
          catchError(error => {
            console.error('Error', error);
            this.notification.error(
              'Error',
              'There was an error retriggering payment status. '
            );
            return of(OrdersActions.retriggerPaymentFailure({ error }));
          })
        )
      )
    )
  );
}
